import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'

const R = {
  display: { fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif", letterSpacing: '0.14em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
}

export default function BleedOutTimer({ onDismiss }) {
  const [secondsLeft, setSecondsLeft] = useState(null)
  const [phase, setPhase] = useState('loading')
  const intervalRef = useRef(null)

  useEffect(() => { fetchDuration(); return () => clearInterval(intervalRef.current) }, [])

  const fetchDuration = async () => {
    const { data } = await supabase.from('m55_platform_settings').select('value').eq('key', 'default_bleed_out_seconds').limit(1)
    const seconds = parseInt(data?.[0]?.value ?? '300')
    setSecondsLeft(seconds); setPhase('active'); startCountdown(seconds)
  }

  const startCountdown = (s) => {
    let remaining = s
    intervalRef.current = setInterval(() => { remaining -= 1; setSecondsLeft(remaining); if (remaining <= 0) { clearInterval(intervalRef.current); setPhase('expired') } }, 1000)
  }

  const handleRevived = () => { clearInterval(intervalRef.current); setPhase('revived'); setTimeout(() => onDismiss(), 1500) }
  const formatTime = (s) => { if (s === null) return '--:--'; const m = Math.floor(Math.max(0, s) / 60); const sec = Math.max(0, s) % 60; return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` }
  const isUrgent = secondsLeft !== null && secondsLeft <= 30

  if (phase === 'loading') return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: '#14181a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: '#5a6a60', ...R.display }}>LOADING...</p>
    </div>
  )

  if (phase === 'revived') return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'radial-gradient(ellipse at 50% 50%, rgba(40,100,60,.8), #14181a)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ fontSize: '48px', marginBottom: '16px' }}>💉</p>
      <p style={{ color: '#9adb6e', fontSize: '24px', fontWeight: 700, ...R.display }}>REVIVED</p>
      <p style={{ color: '#4dd6c2', marginTop: '8px', fontSize: '14px', ...R.display }}>Back in the fight</p>
    </div>
  )

  if (phase === 'expired') return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: '#14181a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', textAlign: 'center' }}>
      <p style={{ fontSize: '60px', marginBottom: '24px' }}>💀</p>
      <p style={{ color: '#e0e8de', fontSize: '28px', fontWeight: 700, ...R.display }}>BLEED OUT</p>
      <p style={{ color: '#8aa090', fontSize: '14px', marginBottom: '32px', ...R.display }}>Walk to FOB or Aid Station to respawn</p>
      <p style={{ color: '#5a6a60', fontSize: '12px', marginBottom: '32px' }}>Keep your dead rag visible</p>
      <button onClick={onDismiss} style={{ background: 'rgba(28, 38, 38, .6)', border: '1px solid rgba(140, 180, 130, .2)', color: '#e0e8de', fontWeight: 700, padding: '16px 32px', fontSize: '16px', cursor: 'pointer', ...R.display, backdropFilter: 'blur(6px)' }}>
        I'VE RESPAWNED
      </button>
    </div>
  )

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', textAlign: 'center', background: isUrgent ? 'radial-gradient(ellipse at 50% 50%, rgba(255,106,61,.15), #14181a)' : '#14181a', transition: 'background 1s' }}>
      <p style={{ color: '#e0e8de', fontSize: '18px', fontWeight: 700, marginBottom: '8px', ...R.display }}>BLEED OUT</p>
      <p style={{ color: '#8aa090', fontSize: '12px', marginBottom: '32px' }}>Call your hit — place dead rag — wait</p>

      <div style={{ fontSize: '96px', fontWeight: 700, marginBottom: '40px', fontVariantNumeric: 'tabular-nums', ...R.mono, color: isUrgent ? '#ff6a3d' : '#e0e8de', textShadow: isUrgent ? '0 0 20px rgba(255,106,61,.5)' : 'none', transition: 'color .5s, text-shadow .5s' }}>
        {formatTime(secondsLeft)}
      </div>

      <p style={{ color: '#5a6a60', fontSize: '12px', marginBottom: '40px' }}>Dead men tell no tales</p>

      <button onClick={handleRevived} style={{ width: '100%', maxWidth: '280px', background: '#4dd6c2', color: '#14181a', fontWeight: 700, padding: '18px 24px', fontSize: '18px', border: 'none', cursor: 'pointer', ...R.display, boxShadow: '0 0 12px rgba(77,214,194,.4)' }}>
        💉 REVIVED
      </button>

      <button onClick={onDismiss} style={{ marginTop: '16px', background: 'none', border: 'none', color: '#5a6a60', fontSize: '12px', cursor: 'pointer' }}>
        Cancel (false alarm)
      </button>
    </div>
  )
}
